import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Building2, Filter, X } from 'lucide-react';
import { searchInstitutions } from '../api/client';
import type { InstitutionType, EntityStatus } from '../types';

const INSTITUTION_TYPES: InstitutionType[] = [
  'CREDIT_INSTITUTION',
  'INVESTMENT_FIRM',
  'INSURANCE_UNDERTAKING',
  'IORP',
  'PAYMENT_INSTITUTION',
  'EMONEY_INSTITUTION',
  'CASP',
  'CCP',
  'CSD',
  'FUND_MANAGER',
  'OTHER',
];

const STATUSES: EntityStatus[] = ['ACTIVE', 'INACTIVE', 'WITHDRAWN', 'MERGED'];

export default function InstitutionSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [showFilters, setShowFilters] = useState(false);

  const currentParams = {
    q: searchParams.get('q') || undefined,
    jurisdiction: searchParams.get('jurisdiction') || undefined,
    institution_type: (searchParams.get('type') as InstitutionType) || undefined,
    status: (searchParams.get('status') as EntityStatus) || 'ACTIVE',
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: 20,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['institutions', 'search', currentParams],
    queryFn: () => searchInstitutions(currentParams),
    enabled: !!currentParams.q || !!currentParams.jurisdiction || !!currentParams.institution_type,
  });

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = new URLSearchParams(searchParams);
      if (query) {
        params.set('q', query);
      } else {
        params.delete('q');
      }
      params.set('page', '1');
      setSearchParams(params);
    },
    [query, searchParams, setSearchParams]
  );

  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      // Reset to page 1 when changing filters, but not when changing page itself
      if (key !== 'page') {
        params.set('page', '1');
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams({ q: query });
  }, [query, setSearchParams]);

  const hasActiveFilters =
    currentParams.jurisdiction || currentParams.institution_type || currentParams.status !== 'ACTIVE';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Search Institutions</h1>
        <p className="mt-2 text-gray-600">
          Search EU financial institutions by name, country, or type
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by institution name..."
            className="input pl-10"
          />
        </div>
        <button type="submit" className="btn-primary">
          Search
        </button>
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary ${showFilters ? 'bg-gray-200' : ''}`}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full">
              !
            </span>
          )}
        </button>
      </form>

      {/* Filters */}
      {showFilters && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Filters</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                value={currentParams.jurisdiction || ''}
                onChange={(e) => updateFilter('jurisdiction', e.target.value || null)}
                placeholder="e.g., DE, FR, NL"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution Type
              </label>
              <select
                value={currentParams.institution_type || ''}
                onChange={(e) => updateFilter('type', e.target.value || null)}
                className="input"
              >
                <option value="">All types</option>
                {INSTITUTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={currentParams.status || 'ACTIVE'}
                onChange={(e) => updateFilter('status', e.target.value)}
                className="input"
              >
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="card p-6 text-center text-red-600">
          Error loading results. Please try again.
        </div>
      ) : data?.institutions.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No institutions found matching your criteria.</p>
        </div>
      ) : data ? (
        <>
          <div className="text-sm text-gray-500">
            Showing {data.institutions.length} of {data.total.toLocaleString()} results
          </div>
          <div className="card divide-y divide-gray-100">
            {data.institutions.map((inst) => (
              <Link
                key={inst.lei}
                to={`/institution/${inst.lei}`}
                className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {inst.legal_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {inst.primary_type.replace(/_/g, ' ')} • {inst.jurisdiction_country}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-xs text-gray-400">{inst.lei}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {data.total > currentParams.limit && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => updateFilter('page', String(currentParams.page - 1))}
                disabled={currentParams.page <= 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {currentParams.page} of {Math.ceil(data.total / currentParams.limit)}
              </span>
              <button
                onClick={() => updateFilter('page', String(currentParams.page + 1))}
                disabled={currentParams.page * currentParams.limit >= data.total}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="card p-12 text-center">
          <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            Enter a search term or apply filters to find institutions.
          </p>
        </div>
      )}
    </div>
  );
}
